import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, Phone, Globe, Navigation, ArrowLeft, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useSearch } from "@/context/SearchContext";
import { StatusBadge } from "@/components/StatusBadge";
import { SaveButton, CrawlButton } from "@/components/VenueActions";
import { ShareButton } from "@/components/ShareButton";
import { VenueMap } from "@/components/VenueMap";
import { FeaturedBadge, Specials } from "@/components/VenueCard";
import { WEEK, DAY_LABELS, fmtWindow, todayKey, isFeatured, directionsUrl, haversine } from "@/lib/hh";

function Hours({ hours }) {
  const today = todayKey();
  return (
    <ul data-testid="venue-hours" className="divide-y divide-zinc-800">
      {WEEK.map((d) => (
        <li key={d} className={`flex justify-between py-2.5 text-sm ${d === today ? "font-bold text-[#fde68a]" : "text-zinc-300"}`}>
          <span>{DAY_LABELS[d]}{d === today ? " · today" : ""}</span>
          <span className={hours?.[d] ? "" : "text-zinc-600"}>{fmtWindow(hours?.[d])}</span>
        </li>
      ))}
    </ul>
  );
}

function Contact({ v }) {
  const site = v.website && (v.website.startsWith("http") ? v.website : `https://${v.website}`);
  return (
    <div className="space-y-3 text-sm">
      <p className="flex gap-2 text-zinc-300"><MapPin size={16} className="shrink-0 text-[#fde68a]" />{v.address}</p>
      {v.phone && <a data-testid="venue-phone" href={`tel:${v.phone}`} className="flex gap-2 text-zinc-300 hover:text-white"><Phone size={16} className="text-[#fde68a]" />{v.phone}</a>}
      {site && <a data-testid="venue-website" href={site} target="_blank" rel="noopener noreferrer" className="flex gap-2 break-all text-zinc-300 hover:text-white"><Globe size={16} className="shrink-0 text-[#fde68a]" />{v.website}</a>}
      <div className="flex flex-wrap gap-2 pt-2">
        <a data-testid="directions-google" href={directionsUrl(v)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 rounded-full bg-[#fde68a] px-4 py-2 text-sm font-bold text-zinc-950"><Navigation size={14} /> Google Maps</a>
        <a data-testid="directions-apple" href={directionsUrl(v, true)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 rounded-full border border-zinc-700 px-4 py-2 text-sm font-semibold text-zinc-200"><Navigation size={14} /> Apple Maps</a>
      </div>
    </div>
  );
}

export default function VenueDetail() {
  const { id } = useParams();
  const { origin } = useSearch();
  const [v, setV] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get(`/venues/${id}`).then((r) => setV(r.data)).catch((e) => setErr(errMsg(e)));
  }, [id]);
  if (err) return <div className="page"><p data-testid="venue-error" className="panel p-6 text-zinc-300">{err}</p></div>;
  if (!v) return <div className="page flex justify-center py-20"><Loader2 className="animate-spin text-[#fde68a]" /></div>;
  const dist = origin && v.lat != null ? haversine(origin, v).toFixed(1) : null;
  return (
    <div className="page">
      <Link to="/" className="mb-5 inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white"><ArrowLeft size={15} /> Back to results</Link>
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            {isFeatured(v) && <FeaturedBadge />}
            <StatusBadge venue={v} testId="venue-status" />
            {dist && <span className="text-sm font-semibold text-zinc-400">{dist} mi away</span>}
          </div>
          <h1 data-testid="venue-name" className="font-heading text-5xl leading-none text-white sm:text-6xl lg:text-7xl">{v.name}</h1>
          <p className="mt-2 text-zinc-400">{v.city}, {v.state}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <SaveButton venue={v} />
          <CrawlButton venue={v} full />
          <ShareButton title={v.name} text={`Happy hour at ${v.name}`} path={`/venue/${v.id}`} testId="venue-share-button" />
        </div>
      </div>
      <div className="mt-8 grid gap-6 lg:grid-cols-5">
        <div className="space-y-6 lg:col-span-3">
          <section className="panel p-6">
            <p className="eyebrow mb-4">Happy hour specials</p>
            <Specials specials={v.specials || []} />
          </section>
          <section className="panel p-6">
            <p className="eyebrow mb-2">Happy hour times</p>
            <Hours hours={v.hours} />
          </section>
        </div>
        <div className="space-y-6 lg:col-span-2">
          {v.lat != null && <VenueMap venues={[v]} className="h-72" />}
          <section className="panel p-6"><Contact v={v} /></section>
          <p className="text-xs text-zinc-500">{v.source === "owner" ? "Specials provided by the venue owner." : "Found by AI. Confirm specials with the venue before you go."}</p>
        </div>
      </div>
    </div>
  );
}
